import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaceLandmarker } from '@mediapipe/tasks-vision';
import { useSession } from '../store/session';
import { submitForm } from '../api';
import { forceFaceDelegate, getActiveFaceDelegate, getFaceLandmarker, preloadFaceAssets } from '../lib/face';
import {
  computeFaceBoundsList,
  getObjectFitCoverTransform,
  mapBoundsToElementRect,
  pickFaceIndexByAnchor,
  pickFaceIndexByClick,
  type ElementRect,
  type FaceAnchor,
  type FaceBounds,
} from '../lib/faceSelection';
import { requestFullscreen } from '../utils/requestFullscreen';
import { isIPad } from '../utils/isIPad';
import { openOrCreateDB } from '../runner/openIDB';
import {
  buildArgusRunnerPath,
  isVideoWorkflow,
  persistArgusWorkflow,
  resolveArgusWorkflow,
} from '../lib/workflow';
import { prepareGalleryStimuli } from '../lib/stimulusPreload';
import { getOrCreateClientRunId } from '../lib/clientRun';
import {
  Alert,
  Button,
  CircularProgress,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';

type FaceGuidanceState =
  | 'searching'
  | 'select-face'
  | 'off-center'
  | 'too-far'
  | 'too-close'
  | 'stabilizing'
  | 'ready';

const GUIDE_BOX_INSET_RATIO = 0.14;
const GUIDE_CENTER_TOLERANCE_RATIO = 0.18;
const GUIDE_MIN_FACE_WIDTH_RATIO = 0.08;
const GUIDE_MIN_FACE_HEIGHT_RATIO = 0.10;
const GUIDE_MAX_FACE_WIDTH_RATIO = 0.86;
const GUIDE_MAX_FACE_HEIGHT_RATIO = 0.96;
const GUIDE_STABLE_FACE_MS = 2000;

function getGuideRect(elementW: number, elementH: number): ElementRect {
  const side = Math.max(1, Math.min(elementW, elementH) * (1 - GUIDE_BOX_INSET_RATIO * 2));
  const x = (elementW - side) / 2;
  const y = (elementH - side) / 2;
  return { x, y, w: side, h: side, cx: x + side / 2, cy: y + side / 2 };
}

function assessFaceGuidance(rect: ElementRect, guide: ElementRect): FaceGuidanceState {
  const centerDx = Math.abs(rect.cx - guide.cx);
  const centerDy = Math.abs(rect.cy - guide.cy);
  if (
    centerDx > guide.w * GUIDE_CENTER_TOLERANCE_RATIO ||
    centerDy > guide.h * GUIDE_CENTER_TOLERANCE_RATIO
  ) {
    return 'off-center';
  }

  const widthRatio = rect.w / guide.w;
  const heightRatio = rect.h / guide.h;
  if (widthRatio < GUIDE_MIN_FACE_WIDTH_RATIO || heightRatio < GUIDE_MIN_FACE_HEIGHT_RATIO) {
    return 'too-far';
  }
  if (widthRatio > GUIDE_MAX_FACE_WIDTH_RATIO || heightRatio > GUIDE_MAX_FACE_HEIGHT_RATIO) {
    return 'too-close';
  }

  return 'ready';
}

export default function Start() {
  const location = useLocation();
  const nav = useNavigate();
  const setStream = useSession((s) => s.setStream);
  const setHasPermission = useSession((s) => s.setHasPermission);
  const setFaceOK = useSession((s) => s.setFaceOK);
  const selectedFaceAnchor = useSession((s) => s.selectedFaceAnchor);
  const setSelectedFaceAnchor = useSession((s) => s.setSelectedFaceAnchor);

  const [landmarkerReady, setLandmarkerReady] = useState(false);
  const [delegate, setDelegate] = useState<'GPU' | 'CPU' | null>(() => getActiveFaceDelegate());
  const [cameraReady, setCameraReady] = useState(false);
  const [faceDetected, setFaceDetected] = useState(false);
  const [facesCount, setFacesCount] = useState(0);
  const [faceGuidance, setFaceGuidance] = useState<FaceGuidanceState>('searching');
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [switchingDelegate, setSwitchingDelegate] = useState(false);
  const [collectionContextReady, setCollectionContextReady] = useState(true);

  // Fullscreen state: true for iPad (inline mode) or when document is actually fullscreen.
  const [fsReady, setFsReady] = useState<boolean>(() => isIPad());
  const [needsFsNudge, setNeedsFsNudge] = useState(false);

  const workflow = useMemo(() => resolveArgusWorkflow(location.search), [location.search]);
  const videoCollectionMode = isVideoWorkflow(workflow.runnerMode);
  const shouldPrimeStimuli = workflow.workflowId === 'screening-v2';
  const [stimulusWarmState, setStimulusWarmState] = useState<'idle' | 'warming' | 'ready' | 'error'>(
    shouldPrimeStimuli ? 'warming' : 'idle',
  );
  const [stimulusWarmError, setStimulusWarmError] = useState<string | null>(null);

  useEffect(() => {
    persistArgusWorkflow(workflow);
  }, [workflow]);

  useEffect(() => {
    if (!videoCollectionMode) {
      setCollectionContextReady(true);
      return;
    }
    try {
      const raw = localStorage.getItem('argus_collection_context');
      const parsed = raw ? JSON.parse(raw) : null;
      const ok = parsed?.version === 1 && parsed?.participant && parsed?.intake;
      setCollectionContextReady(Boolean(ok));
      if (!ok) {
        setError('缺少受试者与参考状态信息。请回到“开始新测试”页面填写信息后再打开筛查流程。');
      }
    } catch {
      setCollectionContextReady(false);
      setError('受试者信息读取失败。请回到“开始新测试”页面重新打开筛查流程。');
    }
  }, [videoCollectionMode]);

  useEffect(() => {
    if (!shouldPrimeStimuli) {
      setStimulusWarmState('idle');
      setStimulusWarmError(null);
      return;
    }

    let active = true;
    setStimulusWarmState('warming');
    setStimulusWarmError(null);

    prepareGalleryStimuli(workflow.configPath)
      .then(() => {
        if (!active) return;
        setStimulusWarmState('ready');
      })
      .catch((error) => {
        if (!active) return;
        setStimulusWarmState('error');
        setStimulusWarmError(error instanceof Error ? error.message : '素材预载失败');
      });

    return () => {
      active = false;
    };
  }, [shouldPrimeStimuli, workflow.configPath]);

  const vref = useRef<HTMLVideoElement>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const landmarkerRef = useRef<FaceLandmarker>();

  const anchorRef = useRef<FaceAnchor | null>(selectedFaceAnchor);
  const faceOkRef = useRef(false);
  const lastBoundsRef = useRef<FaceBounds[]>([]);
  const noFaceSinceRef = useRef<number | null>(null);
  const readyFaceSinceRef = useRef<number | null>(null);
  const didAutoFallbackRef = useRef(false);
  const switchingDelegateRef = useRef(false);
  const detectErrorCountRef = useRef(0);

  const readStoredFormDataId = (): number => {
    try {
      const raw = localStorage.getItem('argus_formDataId');
      if (!raw) return 0;
      const parsed = Number(raw);
      return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : 0;
    } catch {
      return 0;
    }
  };

  const writeStoredFormDataId = (formDataId: number) => {
    try {
      localStorage.setItem('argus_formDataId', String(formDataId));
      localStorage.setItem('argus_run_started_at', String(Date.now()));
    } catch {
      // ignore storage failures (Safari private mode / quota, etc.)
    }
  };

  const countPendingLandmarks = async (formDataId: number): Promise<number> => {
    const db = await openOrCreateDB();
    try {
      const tx = db.transaction('landmarks', 'readonly');
      const index = tx.objectStore('landmarks').index('by-formDataId');
      const count = await index.count(IDBKeyRange.only(formDataId));
      await tx.done;
      return Number.isFinite(count) ? count : 0;
    } finally {
      db.close();
    }
  };

  const purgePendingLandmarks = async (formDataId: number): Promise<void> => {
    const db = await openOrCreateDB();
    try {
      const tx = db.transaction('landmarks', 'readwrite');
      const store = tx.objectStore('landmarks');
      const index = store.index('by-formDataId');
      for (
        let cursor = await index.openCursor(IDBKeyRange.only(formDataId));
        cursor;
        cursor = await cursor.continue()
      ) {
        const pk = cursor.primaryKey as number;
        await store.delete(pk);
      }
      await tx.done;
    } finally {
      db.close();
    }
  };

  useEffect(() => {
    anchorRef.current = selectedFaceAnchor;
  }, [selectedFaceAnchor]);

  // fresh run should not inherit a previous selection
  useEffect(() => {
    setSelectedFaceAnchor(null);
    setFaceOK(false);
    faceOkRef.current = false;
  }, [setFaceOK, setSelectedFaceAnchor]);

  // Load face model
  useEffect(() => {
    let cancelled = false;
    setLandmarkerReady(false);
    void preloadFaceAssets();
    getFaceLandmarker()
      .then((lm) => {
        if (!cancelled) {
          landmarkerRef.current = lm;
          setLandmarkerReady(true);
          setDelegate(getActiveFaceDelegate());
        }
      })
      .catch((e) => setError(`人脸模型加载失败：${e}`));
    return () => {
      cancelled = true;
    };
  }, []);

  async function switchToCpu(reason: 'auto' | 'manual') {
    if (switchingDelegateRef.current) return;
    switchingDelegateRef.current = true;
    setSwitchingDelegate(true);
    setError(null);
    setLandmarkerReady(false);
    try {
      const lm = await forceFaceDelegate('CPU');
      landmarkerRef.current = lm;
      setLandmarkerReady(true);
      setDelegate('CPU');
      if (reason === 'auto') didAutoFallbackRef.current = true;
    } catch (e) {
      setError((e as Error)?.message ?? String(e));
      // restore ready state so the previous landmarker (if any) keeps working
      setLandmarkerReady(!!landmarkerRef.current);
    } finally {
      setSwitchingDelegate(false);
      switchingDelegateRef.current = false;
    }
  }

  // Track fullscreen changes (treat iPad as "ready" always)
  useEffect(() => {
    const onFsChange = () => setFsReady(isIPad() || !!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  async function enableCamera() {
    setError(null);
    if (!collectionContextReady) {
      setError('缺少受试者与参考状态信息。请回到“开始新测试”页面填写信息后再打开筛查流程。');
      return;
    }
    setFaceDetected(false);
    setFacesCount(0);
    setFaceGuidance('searching');
    setSelectedFaceAnchor(null);
    anchorRef.current = null;
    setFaceOK(false);
    faceOkRef.current = false;
    noFaceSinceRef.current = null;
    readyFaceSinceRef.current = null;
    didAutoFallbackRef.current = false;
    detectErrorCountRef.current = 0;

    try {
      // 1) Ask for webcam permission FIRST
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 1280, max: 1280 },
          height: { ideal: 720, max: 720 },
          frameRate: { ideal: 30, max: 30 },
        },
        audio: false,
      });

      // 2) Start preview & mark permission
      if (!vref.current) return;
      vref.current.srcObject = stream;
      await vref.current.play().catch(() => {});
      setStream(stream);
      setHasPermission(true);
      setCameraReady(true);

      // 3) Try to go fullscreen next
      const ok = await requestFullscreen();
      setFsReady(isIPad() || ok || !!document.fullscreenElement);
      if (!(ok || document.fullscreenElement || isIPad())) {
        setNeedsFsNudge(true); // require explicit user click to enter fullscreen
      }
      // Auto face check will start via the effect below once fsReady === true.
    } catch (e) {
      setError((e as Error)?.message ?? '无法访问摄像头。');
    }
  }

  async function enterFullscreenNow() {
    const ok = await requestFullscreen();
    const ready = isIPad() || ok || !!document.fullscreenElement;
    setFsReady(ready);
    if (ready) setNeedsFsNudge(false);
  }

  useEffect(() => {
    let cancelled = false;
    let rafId = 0;
    const last = {
      facesCount: -1,
      faceDetected: false,
      faceGuidance: 'searching' as FaceGuidanceState,
    };

    const video = vref.current;
    const canvas = overlayRef.current;
    if (!cameraReady || !fsReady || !landmarkerReady || !video || !canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let lastDetectionAt = 0;
    let cachedBounds: FaceBounds[] = [];

    const draw = (
      bounds: FaceBounds[],
      selectedIndex: number | null,
      guidance: FaceGuidanceState,
    ) => {
      const elementW = canvas.clientWidth;
      const elementH = canvas.clientHeight;
      const dpr = window.devicePixelRatio || 1;
      const nextW = Math.max(1, Math.round(elementW * dpr));
      const nextH = Math.max(1, Math.round(elementH * dpr));
      if (canvas.width !== nextW || canvas.height !== nextH) {
        canvas.width = nextW;
        canvas.height = nextH;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, elementW, elementH);

      const t = getObjectFitCoverTransform(elementW, elementH, video.videoWidth, video.videoHeight);
      const guide = getGuideRect(elementW, elementH);
      ctx.lineWidth = 2;
      ctx.strokeStyle =
        guidance === 'ready' ? 'rgba(34,197,94,0.95)' : 'rgba(250,204,21,0.92)';
      ctx.setLineDash([8, 8]);
      ctx.strokeRect(guide.x, guide.y, guide.w, guide.h);
      ctx.setLineDash([]);

      for (let i = 0; i < bounds.length; i++) {
        const rect = mapBoundsToElementRect(bounds[i], t);
        const isSelected = selectedIndex === i;
        ctx.lineWidth = isSelected ? 3 : 2;
        ctx.strokeStyle = isSelected
          ? guidance === 'ready'
            ? 'rgba(34,197,94,0.95)'
            : 'rgba(250,204,21,0.95)'
          : 'rgba(255,255,255,0.55)';
        ctx.strokeRect(rect.x, rect.y, rect.w, rect.h);

        if (bounds.length > 1) {
          ctx.fillStyle = isSelected ? 'rgba(34,197,94,0.95)' : 'rgba(255,255,255,0.75)';
          ctx.font =
            '12px system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif';
          ctx.fillText(String(i + 1), rect.x + 4, rect.y + 14);
        }
      }
    };

    const loop = () => {
      if (cancelled) return;
      if (switchingDelegateRef.current) {
        rafId = requestAnimationFrame(loop);
        return;
      }

      const lm = landmarkerRef.current;
      if (!lm) {
        rafId = requestAnimationFrame(loop);
        return;
      }

      const now = performance.now();
      const activeDelegate = getActiveFaceDelegate();
      const detectIntervalMs = activeDelegate === 'CPU' ? 66 : 33;

      if (
        video.readyState >= 2 &&
        (cachedBounds.length === 0 || now - lastDetectionAt >= detectIntervalMs)
      ) {
        lastDetectionAt = now;
        try {
          const res = lm.detectForVideo(video, now);
          const faces = res.faceLandmarks ?? [];
          cachedBounds = computeFaceBoundsList(
            faces as unknown as Array<Array<{ x: number; y: number }>>,
          );
          detectErrorCountRef.current = 0;
        } catch (e) {
          cachedBounds = [];
          detectErrorCountRef.current += 1;
          const msg = String((e as Error)?.message ?? e ?? '');
          const shouldFallback =
            !didAutoFallbackRef.current &&
            activeDelegate === 'GPU' &&
            (msg.includes('dst_tensor_sub_tex_width') ||
              msg.includes('gl_graph_runner') ||
              detectErrorCountRef.current >= 2);
          if (shouldFallback) {
            didAutoFallbackRef.current = true;
            void switchToCpu('auto');
          }
        }
      }
      const bounds = cachedBounds;
      lastBoundsRef.current = bounds;

      const count = bounds.length;

      // Auto-fallback: on some devices GPU delegate initializes but produces empty results.
      // If we see *no* faces for a while, retry with CPU once to improve compatibility.
      if (!didAutoFallbackRef.current && getActiveFaceDelegate() === 'GPU') {
        if (count === 0) {
          if (noFaceSinceRef.current === null) noFaceSinceRef.current = now;
          if (now - (noFaceSinceRef.current ?? now) > 2500) {
            didAutoFallbackRef.current = true;
            void switchToCpu('auto');
          }
        } else {
          noFaceSinceRef.current = null;
        }
      }

      const anchor = anchorRef.current;

      let selectedIndex: number | null = null;
      let canProceed = false;
      let guidance: FaceGuidanceState = 'searching';

      if (count === 0) {
        selectedIndex = null;
        canProceed = false;
        guidance = 'searching';
      } else if (count === 1) {
        selectedIndex = 0;
        const t = getObjectFitCoverTransform(
          canvas.clientWidth,
          canvas.clientHeight,
          video.videoWidth,
          video.videoHeight,
        );
        const selectedRect = mapBoundsToElementRect(bounds[0], t);
        guidance = assessFaceGuidance(selectedRect, getGuideRect(canvas.clientWidth, canvas.clientHeight));
        canProceed = guidance === 'ready';
        if (!anchor) {
          const nextAnchor = { cx: bounds[0].cx, cy: bounds[0].cy };
          anchorRef.current = nextAnchor;
          setSelectedFaceAnchor(nextAnchor);
        }
      } else {
        if (anchor) {
          selectedIndex = pickFaceIndexByAnchor(bounds, anchor);
          const t = getObjectFitCoverTransform(
            canvas.clientWidth,
            canvas.clientHeight,
            video.videoWidth,
            video.videoHeight,
          );
          const selectedRect = mapBoundsToElementRect(bounds[selectedIndex], t);
          guidance = assessFaceGuidance(
            selectedRect,
            getGuideRect(canvas.clientWidth, canvas.clientHeight),
          );
          canProceed = guidance === 'ready';
        } else {
          selectedIndex = null;
          canProceed = false;
          guidance = 'select-face';
        }
      }

      if (guidance === 'ready') {
        if (readyFaceSinceRef.current === null) readyFaceSinceRef.current = now;
        if (now - readyFaceSinceRef.current < GUIDE_STABLE_FACE_MS) {
          guidance = 'stabilizing';
          canProceed = false;
        }
      } else {
        readyFaceSinceRef.current = null;
        canProceed = false;
      }

      draw(bounds, selectedIndex, guidance);

      if (last.facesCount !== count) {
        last.facesCount = count;
        setFacesCount(count);
      }
      if (last.faceDetected !== canProceed) {
        last.faceDetected = canProceed;
        setFaceDetected(canProceed);
      }
      if (last.faceGuidance !== guidance) {
        last.faceGuidance = guidance;
        setFaceGuidance(guidance);
      }
      if (faceOkRef.current !== canProceed) {
        faceOkRef.current = canProceed;
        setFaceOK(canProceed);
      }

      rafId = requestAnimationFrame(loop);
    };

    rafId = requestAnimationFrame(loop);
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [cameraReady, fsReady, landmarkerReady, setFaceOK, setSelectedFaceAnchor]);

  const onPreviewClick = (event: MouseEvent<HTMLCanvasElement>) => {
    const bounds = lastBoundsRef.current;
    if (bounds.length < 2) return;
    const video = vref.current;
    const canvas = overlayRef.current;
    if (!video || !canvas) return;

    const rect = canvas.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const clickY = event.clientY - rect.top;

    const t = getObjectFitCoverTransform(
      rect.width,
      rect.height,
      video.videoWidth,
      video.videoHeight,
    );
    const idx = pickFaceIndexByClick(bounds, clickX, clickY, t);
    if (idx === null) return;

    const anchor = { cx: bounds[idx].cx, cy: bounds[idx].cy };
    anchorRef.current = anchor;
    setSelectedFaceAnchor(anchor);
  };

  async function startTest() {
    if (starting) return;
    getOrCreateClientRunId();
    // Be robust: ensure we’re fullscreen before proceeding (no-op on iPad).
    if (!fsReady) {
      const ok = await requestFullscreen();
      setFsReady(isIPad() || ok || !!document.fullscreenElement);
      if (!(ok || document.fullscreenElement || isIPad())) {
        setNeedsFsNudge(true);
        return;
      }
    }
    if (!faceDetected) {
      setError(faceGuidanceMessage);
      return;
    }
    if (shouldPrimeStimuli && stimulusWarmState !== 'ready') {
      setError(stimulusWarmError ?? '测试素材仍在预载，请等按钮变为“开始测试”后再开始。');
      return;
    }
    setStarting(true);
    setError(null);
    try {
      if (!videoCollectionMode) {
        const existing = readStoredFormDataId();
        if (existing > 0) {
          const pending = await countPendingLandmarks(existing);
          if (pending > 0) {
            await purgePendingLandmarks(existing);
          }
        }

        const ident = `run-${Math.floor(Date.now() / 1000)}`;
        const { formDataId } = await submitForm(ident);
        writeStoredFormDataId(formDataId);
      } else {
        try {
          localStorage.removeItem('argus_formDataId');
        } catch {
          // ignore storage failures
        }
      }
      nav(buildArgusRunnerPath('/runner/gallery', workflow.workflowId));
    } catch (e) {
      setError((e as Error)?.message ?? '无法创建测验运行，请稍后重试。');
    } finally {
      setStarting(false);
    }
  }

  const activeStep = !cameraReady ? 0 : !fsReady ? 1 : !faceDetected ? 2 : 3;
  const needsSelection = facesCount > 1 && !selectedFaceAnchor;
  const guideMaskClassName =
    faceGuidance === 'ready'
      ? 'runner-guide-mask is-ready'
      : faceGuidance === 'searching'
        ? 'runner-guide-mask'
        : 'runner-guide-mask is-warning';

  const faceGuidanceMessage = (() => {
    if (!cameraReady) return '请先启用摄像头。';
    if (!fsReady) return '进入全屏后继续调整受试者位置。';
    if (!landmarkerReady) return '摄像头已启用，正在完成首次人脸模型加载，请保持页面打开。';
    switch (faceGuidance) {
      case 'ready':
        return '位置合适，可以开始测试。';
      case 'select-face':
        return '检测到多张人脸，请点击方框中的受试者人脸。';
      case 'off-center':
        return '请将脸移到方框中央，保持双眼都在框内。';
      case 'too-far':
        return '请稍微靠近一些，脸部不需要填满方框，但不能太小。';
      case 'too-close':
        return '请稍微后退一点，让整张脸完整落在方框内。';
      case 'stabilizing':
        return '已检测到合适位置，请保持脸部稳定 2 秒后开始。';
      case 'searching':
      default:
        return '请把脸放进中央方框，系统会自动检测。';
    }
  })();

  const preloadStatusMessage = (() => {
    if (!shouldPrimeStimuli) return null;
    if (stimulusWarmState === 'warming') return '正在预载测试素材；完成后“开始测试”按钮会自动可用。';
    if (stimulusWarmState === 'ready') return '测试素材已预载完成。';
    return null;
  })();

  const stimulusReady = !shouldPrimeStimuli || stimulusWarmState === 'ready';
  const showEnableCameraAction = !cameraReady;
  const showFullscreenAction = cameraReady && !fsReady && needsFsNudge;
  const showCpuFallbackAction = cameraReady && fsReady && facesCount === 0 && delegate !== 'CPU' && !switchingDelegate;
  const showStartAction = cameraReady && fsReady;
  const loadingDetector = cameraReady && fsReady && !landmarkerReady;
  const startButtonDisabled =
    !collectionContextReady || !landmarkerReady || !faceDetected || !stimulusReady || starting;

  return (
    <div className="stage" aria-label="Runner start">
      <div className="runner-overlay">
        <div className="runner-ui runner-ui-start">
          <div className="runner-preview-stage" aria-label="Camera alignment preview">
            <video ref={vref} className="runner-preview" playsInline muted />
            <canvas
              ref={overlayRef}
              className={`runner-preview-overlay${needsSelection ? ' clickable' : ''}`}
              onClick={onPreviewClick}
              aria-label="Face selection overlay"
            />
            <div className={guideMaskClassName} aria-hidden>
              <div className="runner-guide-pane top" />
              <div className="runner-guide-pane right" />
              <div className="runner-guide-pane bottom" />
              <div className="runner-guide-pane left" />
              <div className="runner-guide-square" />
            </div>
          </div>

          <div className="runner-start-panel">
            <Stack spacing={1} className="runner-start-copy">
              <Typography variant="h5" component="h2" sx={{ textAlign: 'center', fontWeight: 800 }}>
                {workflow.label}准备
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85, textAlign: 'center' }}>
                请先让受试者坐到屏幕前，将脸放到中央方框内，再开始测试。
                {workflow.liveDiagnosis
                  ? ' 该流程保持旧版 landmark 采集与现场判读逻辑。'
                  : ' 该流程会保存完整视频与刺激时间线，并生成即时综合筛查摘要。'}
                若检测到多张人脸，请点击方框中的受试者人脸。
              </Typography>

              <Alert
                severity={faceGuidance === 'ready' ? 'success' : 'info'}
                sx={{
                  width: '100%',
                  '.MuiAlert-message': { width: '100%' },
                }}
              >
                {faceGuidanceMessage}
              </Alert>

              {shouldPrimeStimuli && stimulusWarmState === 'error' && stimulusWarmError && (
                <Alert severity="error" sx={{ width: '100%' }}>
                  {stimulusWarmError}
                </Alert>
              )}

              <Stepper
                activeStep={activeStep}
                alternativeLabel
                className="runner-start-stepper"
                sx={{
                  width: '100%',
                  mb: 0,
                  '.MuiStepLabel-label': {
                    fontSize: { xs: '0.72rem', sm: '0.82rem' },
                    mt: 0.5,
                  },
                  '.MuiStepIcon-root': { fontSize: { xs: '1.35rem', sm: '1.5rem' } },
                }}
              >
                <Step>
                  <StepLabel>摄像头</StepLabel>
                </Step>
                <Step>
                  <StepLabel>全屏</StepLabel>
                </Step>
                <Step>
                  <StepLabel>人脸检测</StepLabel>
                </Step>
                <Step>
                  <StepLabel>开始</StepLabel>
                </Step>
              </Stepper>
            </Stack>

            <div className="runner-start-status">
              {delegate && (
                <Typography variant="caption" className="runner-start-meta">
                  人脸引擎：{delegate}
                  {switchingDelegate ? '（切换中…）' : ''}
                </Typography>
              )}

              {!delegate && !landmarkerReady && (
                <Typography variant="caption" className="runner-start-meta">
                  人脸引擎：正在初始化
                </Typography>
              )}

              {preloadStatusMessage && (
                <Typography variant="caption" className="runner-start-meta">
                  {preloadStatusMessage}
                </Typography>
              )}

              {loadingDetector ? (
                <div className="runner-start-status-inline">
                  <CircularProgress size={18} />
                  <Typography variant="body2">正在加载人脸模型，摄像头已可用…</Typography>
                </div>
              ) : cameraReady && fsReady && faceDetected && !stimulusReady ? (
                <div className="runner-start-status-inline">
                  <CircularProgress size={18} />
                  <Typography variant="body2">人脸位置已就绪，正在完成素材预载…</Typography>
                </div>
              ) : cameraReady && fsReady && facesCount === 0 ? (
                <div className="runner-start-status-inline">
                  <CircularProgress size={18} />
                  <Typography variant="body2">正在检测人脸与位置…</Typography>
                </div>
              ) : null}

              {needsSelection && (
                <Typography variant="body2" className="runner-start-status-copy">
                  检测到多张人脸，请点击中央方框中的受试者人脸。
                </Typography>
              )}
            </div>

            <div className="runner-start-actions">
              {showEnableCameraAction && (
                <Button
                  onClick={enableCamera}
                  disabled={!collectionContextReady}
                  variant="contained"
                  size="large"
                  fullWidth
                >
                  {!collectionContextReady
                    ? '请先从开始新测试页面打开'
                    : landmarkerReady
                      ? '启用摄像头'
                      : '先启用摄像头（模型后台加载中）'}
                </Button>
              )}

              {showFullscreenAction && (
                <Button onClick={enterFullscreenNow} variant="contained" size="large" fullWidth>
                  进入全屏
                </Button>
              )}

              {showCpuFallbackAction && (
                <Button variant="outlined" size="large" onClick={() => switchToCpu('manual')} fullWidth>
                  检测不到？切换兼容模式
                </Button>
              )}

              {showStartAction && (
                <Button
                  onClick={startTest}
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={startButtonDisabled}
                >
                  {starting
                    ? '正在准备…'
                    : !landmarkerReady
                      ? '人脸模型加载中…'
                      : !faceDetected
                        ? '请先完成位置检查'
                        : !stimulusReady
                          ? '素材预载中…'
                          : '开始测试'}
                </Button>
              )}
            </div>

            {error && (
              <Alert severity="error" sx={{ width: '100%' }}>
                {error}
              </Alert>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
